import type { AxiosRequestConfig } from "axios";

import { httpClient } from "@/lib/axios";
import type { ApiSuccessResponse, PaginationMeta } from "@/types/api";

/**
 * The typed request layer every feature's API module builds on —
 * `features/<name>/services/` calls these, never `httpClient`/`axios`
 * directly. Its one job: unwrap the `{ success, message, data, meta? }`
 * envelope (src/types/api.ts) so calling code works with `T` directly
 * instead of re-unwrapping `.data.data` in every feature. The failure path
 * needs no handling here — src/lib/interceptors.ts already rejects every
 * failed request as a single `ApiError` shape before it reaches this layer.
 *
 * Per docs/architecture/frontend-architecture.md's folder split: this file
 * is the cross-feature piece that belongs in `services/`; a feature's own
 * endpoints (e.g. `GET /products/:id`) get a small typed wrapper in that
 * feature's own `services/` calling `baseApi.get(...)`, not a copy of this
 * file.
 */
async function unwrap<T>(request: Promise<{ data: ApiSuccessResponse<T> }>): Promise<T> {
  const response = await request;
  return response.data.data;
}

/**
 * Separate from `get` because a paginated endpoint's `meta` (page/limit/
 * totalItems/totalPages) is real data a list page needs — `get`'s plain
 * unwrap would silently discard it.
 */
async function unwrapPaginated<T>(
  request: Promise<{ data: ApiSuccessResponse<T[]> }>,
): Promise<{ data: T[]; meta: PaginationMeta }> {
  const response = await request;
  if (!response.data.meta) {
    throw new Error("Expected a paginated response, but the server didn't include `meta`.");
  }
  return { data: response.data.data, meta: response.data.meta };
}

export const baseApi = {
  get: <T>(url: string, config?: AxiosRequestConfig) =>
    unwrap(httpClient.get<ApiSuccessResponse<T>>(url, config)),

  getPaginated: <T>(url: string, config?: AxiosRequestConfig) =>
    unwrapPaginated<T>(httpClient.get<ApiSuccessResponse<T[]>>(url, config)),

  post: <T, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig) =>
    unwrap(httpClient.post<ApiSuccessResponse<T>>(url, body, config)),

  patch: <T, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig) =>
    unwrap(httpClient.patch<ApiSuccessResponse<T>>(url, body, config)),

  put: <T, TBody = unknown>(url: string, body?: TBody, config?: AxiosRequestConfig) =>
    unwrap(httpClient.put<ApiSuccessResponse<T>>(url, body, config)),

  delete: <T = void>(url: string, config?: AxiosRequestConfig) =>
    unwrap(httpClient.delete<ApiSuccessResponse<T>>(url, config)),
};
